import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface BorderProgressBarProps {
  strokeWidth: number;
  strokeColor: string | { strokeColorStart: string; strokeColorEnd: string };
  progress: number;
}

/**
 * A React component that renders a border-style progress bar using SVG.
 *
 * It overlays a stroked path around its parent container, optionally animating
 * the stroke to show a isLoading state. When not isLoading, it visually represents
 * progress via a partial stroke fill.
 *
 * The component automatically adapts to the dimensions and border radius of its parent.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {number} props.strokeWidth - The thickness of the border stroke
 * @param {string} props.strokeColor - The color of the stroke
 * @param {number} props.progress - A number between 0 and 1 representing progress percentage
 * @param {boolean} [props.isLoading=false] - Whether to show an animated isLoading indicator
 */
function BorderProgressBar({
  strokeWidth,
  strokeColor,
  progress,
}: BorderProgressBarProps) {
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
    borderRadius: 0,
  });

  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const updateDimensions = () => {
      if (svgRef.current?.parentElement) {
        const { parentElement } = svgRef.current;
        const { width, height } = parentElement.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(parentElement);
        const borderRadius = parseInt(
          computedStyle.borderRadius.replace('px', ''),
          10
        );

        parentElement.style.position = 'relative';

        setDimensions({
          width: width - strokeWidth / 2,
          height: height - strokeWidth / 2,
          borderRadius,
        });
      }
    };

    updateDimensions();
  }, [strokeWidth]);

  const createSvgStrokeForBox = (
    width: number,
    height: number,
    borderRadius: number
  ) => {
    const r = Math.min(borderRadius, width / 2, height / 2);
    const midX = width / 2;

    return `
    M ${midX},0
    H ${width - r}
    A ${r},${r} 0 0 1 ${width},${r}
    V ${height - r}
    A ${r},${r} 0 0 1 ${width - r},${height}
    H ${r}
    A ${r},${r} 0 0 1 0,${height - r}
    V ${r}
    A ${r},${r} 0 0 1 ${r},0
    H ${midX}
  `;
  };

  const calculateStrokeLength = useCallback(
    (width: number, height: number, borderRadius: number) => {
      const r = Math.min(borderRadius, width / 2, height / 2);
      const straightLength = (width - 2 * r) * 2 + (height - 2 * r) * 2;
      const cornerLength = ((2 * Math.PI * r) / 4) * 4;

      return straightLength + cornerLength;
    },
    []
  );

  const strokeLength = useMemo(
    () =>
      calculateStrokeLength(
        dimensions.width,
        dimensions.height,
        dimensions.borderRadius
      ),
    [dimensions, calculateStrokeLength]
  );

  // CSS Keyframes for continuous motion animation
  const animationStyle = `
    @keyframes circlular-motion {
      0% {
        stroke-dasharray: ${strokeLength * 0.1} ${strokeLength * 0.9};
        stroke-dashoffset: 0;
      }
      50% {
        stroke-dasharray: ${strokeLength * 0.5} ${strokeLength * 0.5};
        stroke-dashoffset: -${strokeLength * 0.25};
      }
      100% {
        stroke-dasharray: ${strokeLength * 0.1} ${strokeLength * 0.9};
        stroke-dashoffset: -${strokeLength};
      }
    }
  `;

  // Always render static progress; do not animate when loading
  const clampedProgress = Math.max(0, Math.min(1, progress));
  const strokeDasharray = `${clampedProgress * strokeLength} ${strokeLength}`;
  const strokeDashoffset = 0;

  const stroke =
    typeof strokeColor === 'string' ? strokeColor : 'url(#progressGradient)';

  // Avoid initial mount transition: enable transition only after first
  // successful measurement-render cycle completes
  const hasMeasuredOnceRef = useRef(false);

  useEffect(() => {
    if (
      !hasMeasuredOnceRef.current &&
      dimensions.width > 0 &&
      dimensions.height > 0
    ) {
      hasMeasuredOnceRef.current = true;
    }
  }, [dimensions.width, dimensions.height]);

  const transitionStyle = hasMeasuredOnceRef.current
    ? 'stroke-dasharray 0.5s ease'
    : undefined;

  return (
    <>
      <svg
        ref={svgRef}
        width={dimensions.width + strokeWidth}
        height={dimensions.height + strokeWidth}
        viewBox={`-${strokeWidth / 2} -${strokeWidth / 2} ${
          dimensions.width + strokeWidth
        } ${dimensions.height + strokeWidth}`}
        style={{
          position: 'absolute',
          zIndex: 1,
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}>
        {/* Define gradient */}
        {typeof strokeColor === 'object' && (
          <defs>
            <linearGradient
              id='progressGradient'
              x1='0%'
              y1='0%'
              x2='100%'
              y2='0%'>
              <stop offset='0%' stopColor={strokeColor.strokeColorStart} />
              <stop offset='100%' stopColor={strokeColor.strokeColorEnd} />
            </linearGradient>
          </defs>
        )}
        <path
          strokeWidth={strokeWidth}
          stroke={stroke}
          fill='none'
          style={{
            strokeDasharray,
            strokeDashoffset,
            animation: undefined,
            transition: transitionStyle,
          }}
          d={createSvgStrokeForBox(
            dimensions.width,
            dimensions.height,
            dimensions.borderRadius
          )}
        />
      </svg>
      <style>{animationStyle}</style>
    </>
  );
}

BorderProgressBar.defaultProps = {};

export default BorderProgressBar;
